import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, Form, Input } from 'antd';
import axios from 'axios';
import { LogoutOutlined, EditOutlined, CopyOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Profile.jsx
// A simple, professional profile card that reads Full Name, Email and Role from localStorage.
// Adds 'Change Password' modal which calls a backend endpoint to update password.

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();

  // read values (kept as simple strings in localStorage)
  const fullName = localStorage.getItem('fullName') || '';
  const email = localStorage.getItem('email') || '';
  const role = localStorage.getItem('role') || '';
  const token = localStorage.getItem('token')

  const [changeVisible, setChangeVisible] = useState(false);
  const [changing, setChanging] = useState(false);
  const [form] = Form.useForm();


  useEffect(() => {
    const verifyUser = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/user/check-user`,
          { email, role, token }
        );
        if (!res.data.success) {
          const errMsg = res?.data?.message || 'Please Login Again.';
          toast.error(errMsg);
          localStorage.clear();
          navigate('/login');
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'Please Login Again.';
        toast.error(msg);
        localStorage.clear();
        navigate('/login');
      }
    };
    verifyUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname, token, email, role]);

  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tempToken');
    localStorage.removeItem('email');
    localStorage.removeItem('fullName');
    localStorage.removeItem('role');
    toast.success('Logged out successfully');
    setTimeout(() => navigate('/login'), 700);
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success('Email copied to clipboard');
    } catch (e) {
      toast.error('Unable to copy');
    }
  };

  const initials = (fullName || email || 'U')
    .split(' ')
    .map(s => s.charAt(0).toUpperCase())
    .slice(0,2)
    .join('');

  const openChange = () => {
    form.resetFields();
    setChangeVisible(true);
  };

  const submitChange = async () => {
    try {
      const values = await form.validateFields();
      const { currentPassword, newPassword } = values;
      setChanging(true);
      const token = localStorage.getItem('token');
      // call backend endpoint - adjust path if needed
      const res = await fetch(`${process.env.REACT_APP_API_URL}/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to change password');
      toast.success(data?.message || 'Password changed successfully');
      setChangeVisible(false);
      form.resetFields();
     
    } catch (err) {
      toast.error(err?.message || 'Failed to change password');
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="max-w-6xl p-6 mx-auto">
       <ToastContainer
              position="bottom-left"
              autoClose={5000}
              hideProgressBar={true}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
              className="!mb-6 !ml-4"
              toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
            />
      

      <div className="overflow-hidden bg-white border border-gray-100 shadow-sm dark:bg-gray-800 dark:border-gray-700 rounded-2xl">
        <div className="flex flex-col items-center gap-6 p-8 md:flex-row">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center text-2xl font-extrabold text-white rounded-full shadow-md w-28 h-28 bg-gradient-to-br from-indigo-500 to-purple-600">
              {initials}
            </div>
          </div>

          {/* Details */}
          <div className="flex-1 w-full">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{fullName || '—'}</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">{email || 'No email available'}</p>

                <div className="flex items-center gap-3 mt-3">
                  <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">{role || 'USER'}</span>

                  <button onClick={handleCopyEmail} className="inline-flex items-center gap-2 px-3 py-1 text-sm text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50">
                    <CopyOutlined /> Copy email
                  </button>

                  <Button icon={<EditOutlined />} onClick={openChange} className="ml-2">
                    Change Password
                  </Button>

                </div>
              </div>

              <div className="flex gap-3">
                <Button danger icon={<LogoutOutlined />} onClick={handleLogout}>
                  Logout
                </Button>
              </div>
            </div>

            {/* small stats row */}
            <div className="grid grid-cols-2 gap-4 mt-6 md:grid-cols-4">
              <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900">
                <div className="text-sm text-gray-500">Role</div>
                <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{role || '—'}</div>
              </div>
            </div>

          </div>
        </div>

       
      </div>

      <Modal title="Change Password" open={changeVisible} onCancel={() => setChangeVisible(false)} onOk={submitChange} okText={changing ? 'Changing...' : 'Change Password'} okButtonProps={{ disabled: changing }}>
        <Form form={form} layout="vertical">
          <Form.Item name="currentPassword" label="Current password" rules={[{ required: true, message: 'Please enter current password' }]}>
            <Input.Password />
          </Form.Item>

          <Form.Item name="newPassword" label="New password" rules={[{ required: true, message: 'Please enter new password' }, { min: 6, message: 'Password must be at least 6 characters' }]}>
            <Input.Password />
          </Form.Item>

          <Form.Item name="confirmPassword" label="Confirm password" dependencies={["newPassword"]} rules={[{ required: true, message: 'Please confirm new password' }, ({ getFieldValue }) => ({ validator(_, value) { if (!value || getFieldValue('newPassword') === value) { return Promise.resolve(); } return Promise.reject(new Error('Passwords do not match')); }, })]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
